'use client';

import { memo } from 'react';
import { Card } from '@components/common/Cards/Card';
import {
  BriefcaseIcon,
  CurrencyEuroIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { StatusBar } from '@components/templates/my-jobs/Card/partials/StatusBar';
import { Tags } from '@components/templates/my-jobs/Card/partials/Tags';
import { cn } from '@utils/cn';
import { Anchor } from '@components/common';
import { useTranslation } from 'next-i18next';
import { Actions } from '@components/templates/my-jobs/Card/partials/Actions';
import { ConversionButton } from '@components/templates/my-jobs/Card/partials/ConversionButton';
import { getI18nLinkHref } from '@services/i18nPaths';

interface MyJobCardProps {
  job: ApiSchema<'MyJobCard'>;
  revalidate?: (jobSlug: string, oldStatus: string, newStatus: string) => void;
  beforeModalOpen?: () => void;
  locale: Locale;
  defaultConversionPackageId?: string;
}

const MyJobCard: React.FC<MyJobCardProps> = (props: MyJobCardProps) => {
  const {
    job, revalidate, beforeModalOpen, locale, defaultConversionPackageId,
  } = props;
  const { t } = useTranslation('common');


  return (
    <Card
      className={cn(
        'group/myJobCard relative overflow-hidden rounded bg-surface hover:shadow-jobActionsContainer can-hover:transition-shadow can-hover:duration-200 border border-y-3 border-transparent',
        job.needsHighlight && 'border-secondary/50',
      )}
    >
      <Card.Body className="grid grid-rows-[1fr_auto_auto] gap-4 px-4 py-6">

        <div>
          <StatusBar job={job} locale={locale} hasShare />
          <Anchor styling="none" href={getI18nLinkHref(`/mc/job/${job.slug}/applicants/input-flow`, locale)}>
            <h3 className="text-lg font-bold">{job.title}</h3>
          </Anchor>
        </div>
        <div className="flex flex-wrap gap-2">
          <Tags job={job} />
        </div>

        <div>
          {job.conversionEligible
          && (
          <div className="flex justify-between gap-2">
            <ConversionButton defaultConversionPackageId={defaultConversionPackageId} revalidate={revalidate} job={job} />
          </div>
          )}
        </div>


        <div className="flex flex-col gap-1 text-sm">
          <div className="flex items-center gap-2">
            <BriefcaseIcon className="size-4 shrink-0" />
            <p>{job.companyName}</p>
          </div>
          <div className="flex items-center gap-2">
            <CurrencyEuroIcon className="size-4 shrink-0" />
            <div className="flex items-center gap-1">
              <div>{job.salaryRange}</div>
              <div>
                {job.salaryConfidential && t('my-jobs.card.salary.estimated')}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MapPinIcon className="size-4 shrink-0" />
            <p className="w-0 flex-1 truncate">{job.location}</p>
          </div>
        </div>
      </Card.Body>
      <Card.Footer className="z-2 w-full bg-surface group-hover/myJobCard:-translate-y-full group-hover/myJobCard:opacity-100 can-hover:absolute can-hover:top-full can-hover:opacity-0 can-hover:transition-all can-hover:duration-200">
        <div className="px-4 pb-6 can-hover:pt-8">
          <Actions
            job={job}
            revalidate={revalidate}
            beforeModalOpen={beforeModalOpen}
          />
        </div>
      </Card.Footer>
    </Card>
  );
};

export const MemoizedMyJobCard = memo(MyJobCard);
